/* One customer, opened from the Customers table, search, or any link to
   /admin/customers?user=<id>. Reads the user from the shared customers list,
   so every change made here shows up in the table too. */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle, Calendar, Globe, Mail, MoreHorizontal, ShieldCheck, ShieldOff, Trash2,
  UserCog, UserMinus,
} from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  api, countryFlag, displayName, errorMessage, fmtDate, fmtDateTime, fmtInt, fmtPct, humanize,
  hasKids, KIDS_LABEL, pctOf, PLAN_LABEL, PLAN_TONE, planOf, ROLE_LABEL, timeAgo, toList, useQuery, useUsers,
} from './lib';
import {
  Avatar, BarList, Btn, CopyButton, EmptyState, ErrorState, KeyValue, Notice, Pill, Segmented,
  StatCard, TextInput, useConfirm,
} from './ui';
import { fetchGestures, GestureTimeline, matchesOutcome, outcomeOptions } from './gestures';

const TABS = ['profile', 'activity', 'gestures', 'billing', 'system'];

const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : null);
const minutes = (v) => (v ? `${v} min` : null);

function Chips({ values, tone = 'neutral' }) {
  const list = toList(values);
  if (!list.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {list.map((v) => <Pill key={v} tone={tone}>{humanize(v)}</Pill>)}
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold text-foreground">{title}</h3>
      {children}
    </section>
  );
}

function hasOnboardingAnswers(u) {
  const scalars = [
    u.relationship_type, u.relationship_status, u.partner_name, u.age_cluster, u.work_schedule,
    u.referral_source, u.birth_date, u.relationship_duration_months,
  ];
  const lists = [u.motivation, u.improvement_areas, u.tension_points, u.children_ages].map(toList);
  return scalars.some((v) => v !== null && v !== undefined && v !== '') || lists.some((l) => l.length > 0);
}

function ProfileTab({ user: u }) {
  return (
    <div className="space-y-6">
      {u.onboarding_completed && !hasOnboardingAnswers(u) && (
        <Notice title="Onboarded, but no answers were saved">
          Every onboarding field is empty, so only the defaults are stored. The onboarding probably
          submitted an empty payload (skipped questions or stale saved progress).
        </Notice>
      )}
      <Block title="Relationship">
        <KeyValue items={[
          { label: 'Status', value: humanize(u.relationship_status || u.relationship_type) },
          { label: 'Partner', value: u.partner_name },
          { label: 'Together for', value: u.relationship_duration_months ? `${u.relationship_duration_months} months` : null },
          { label: 'Kids', value: KIDS_LABEL[u.kids_cluster] || humanize(u.kids_cluster) },
          // Onboarding doesn't ask how many; a later in-app question does,
          // and until it's answered the stored 0 means "not asked".
          {
            label: 'Number of children',
            value: Number(u.number_of_children) > 0 ? String(u.number_of_children) : hasKids(u) ? 'Not asked yet' : null,
          },
          toList(u.children_ages).length > 0 && { label: 'Children’s ages', value: toList(u.children_ages).join(', ') },
        ]} />
      </Block>
      <Block title="About him">
        <KeyValue items={[
          { label: 'Age group', value: u.age_cluster },
          { label: 'Birth date', value: u.birth_date ? fmtDate(u.birth_date) : null },
          { label: 'Work schedule', value: humanize(u.work_schedule) },
          { label: 'Time per day', value: minutes(u.time_budget_minutes) },
          { label: 'Daily availability', value: minutes(u.daily_availability_minutes) },
          { label: 'Tone', value: humanize(u.tone_preference) },
          { label: 'Language', value: u.language?.toUpperCase() },
          { label: 'Time zone', value: u.timezone },
        ]} />
      </Block>
      <Block title="Goals">
        <KeyValue items={[
          { label: 'Focus', value: humanize(u.focus) },
          { label: 'Heard about us', value: humanize(u.referral_source) },
        ]} />
        {toList(u.motivation).length > 0 && (
          <div><p className="mb-1.5 text-xs text-muted-foreground">Why he joined</p><Chips values={u.motivation} /></div>
        )}
        {toList(u.improvement_areas).length > 0 && (
          <div><p className="mb-1.5 text-xs text-muted-foreground">Wants to improve</p><Chips values={u.improvement_areas} /></div>
        )}
      </Block>
      <Block title="Pain points">
        <KeyValue items={[
          { label: 'On autopilot', value: humanize(u.pain_autopilot) },
          { label: 'Low energy', value: humanize(u.pain_energy) },
          { label: 'Gestures', value: humanize(u.pain_gestures) },
          { label: 'Disconnected', value: humanize(u.pain_connection) },
        ]} />
        {toList(u.tension_points).length > 0 && (
          <div><p className="mb-1.5 text-xs text-muted-foreground">Tension points</p><Chips values={u.tension_points} tone="danger" /></div>
        )}
      </Block>
      <Block title="Later questions">
        <KeyValue items={[
          { label: 'Schedule type', value: humanize(u.schedule_type) },
          { label: 'Time together', value: humanize(u.together_time || u.time_together) },
          { label: 'Home load', value: humanize(u.home_load || u.household_management) },
          { label: 'Main tension', value: humanize(u.focus_tension) },
          { label: 'Communication', value: humanize(u.communication_level) },
          { label: 'Stress', value: humanize(u.stress_level) },
          { label: 'Intimacy', value: humanize(u.intimacy_level) },
        ]} />
      </Block>
    </div>
  );
}

function ActivityTab({ user: u }) {
  const [period, setPeriod] = useState('month');
  const { data: m, error, loading, refetch } = useQuery(
    `user-metrics:${u.user_id}:${period}`,
    () => api.get(`/metrics/${u.user_id}?period=${period}`),
  );
  const categories = Object.entries(m?.category_breakdown || {})
    .map(([label, value]) => ({ label: humanize(label), value }))
    .sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-6">
      <Block title="Recent activity">
        <KeyValue items={[
          { label: 'Last gesture done', value: u.last_action_date ? fmtDate(u.last_action_date) : null },
          { label: 'Last gesture received', value: u.last_suggestion_date ? fmtDate(u.last_suggestion_date) : null },
          { label: 'Skips in a row', value: u.consecutive_skips != null ? String(u.consecutive_skips) : null },
          { label: 'Garden level', value: u.forest_level != null ? String(u.forest_level) : null },
          { label: 'Daily reminder', value: yesNo(u.daily_reminder_enabled ?? u.reminder_enabled) },
          { label: 'Reminder time', value: u.reminder_time || u.reminder_time_local },
          { label: 'Check-in time', value: u.checkin_time },
          { label: 'Event reminders', value: yesNo(u.event_reminders_enabled) },
        ]} />
      </Block>
      <Block title="Gesture totals">
        <Segmented
          value={period}
          onChange={setPeriod}
          options={[
            { value: 'week', label: '7 days' },
            { value: 'month', label: '30 days' },
            { value: 'all', label: 'All time' },
          ]}
        />
        {error ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : !m || loading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[84px] rounded-xl" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard label="Completion" value={fmtPct(m.completion_rate, 0)} hint={`${fmtInt(m.actions_completed)} of ${fmtInt(m.total_actions)}`} />
              <StatCard label="Skipped" value={fmtInt(m.actions_skipped)} />
              <StatCard label="Streak" value={`${fmtInt(m.current_streak)} d`} hint={`Best ${fmtInt(m.best_streak)} d`} />
              <StatCard label="Check-ins" value={fmtInt(m.checkins_count)} hint={m.avg_mood != null ? `Mood ${Number(m.avg_mood).toFixed(1)}` : undefined} />
            </div>
            {categories.length > 0 && (
              <div className="rounded-xl border border-border p-4">
                <p className="mb-3 text-xs font-medium text-muted-foreground">Done by category</p>
                <BarList items={categories} />
              </div>
            )}
          </>
        )}
      </Block>
    </div>
  );
}

function GesturesTab({ user: u }) {
  const [filter, setFilter] = useState('all');
  const { data, error, loading, refetch } = useQuery(
    `user-gestures:${u.user_id}`,
    () => fetchGestures({ user_id: u.user_id }),
  );

  if (error) return <ErrorState error={error} onRetry={refetch} />;
  if (!data) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[84px] rounded-xl" />)}
        </div>
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }

  const c = { ...data.counts, total: data.total };
  const given = c.total - (c.swapped || 0);
  const settled = given - (c.waiting || 0);
  const tracking = { since: data.tracking_since, until: data.tracking_until };

  if (c.total === 0) {
    return <EmptyState title="No gestures yet" description="Gestures appear here once the app has generated one for this person." />;
  }

  return (
    <div className={loading ? 'space-y-5 opacity-70 transition-opacity' : 'space-y-5'}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Given" value={fmtInt(given)} hint={c.swapped ? `+${fmtInt(c.swapped)} swapped for another` : 'None swapped'} />
        <StatCard label="Done" value={fmtInt(c.done)} hint={settled > 0 ? `${fmtPct(pctOf(c.done, settled), 0)} of past gestures` : undefined} tone={c.done > 0 ? 'success' : undefined} />
        <StatCard label="No answer" value={fmtInt(c.no_answer)} hint="Never marked done or skipped" />
        <StatCard
          label="Skipped"
          value={fmtInt((c.skipped || 0) + (c.disliked || 0))}
          hint={c.already_does ? `${fmtInt(c.already_does)} already does it` : 'Tapped skip'}
        />
      </div>
      <div className="space-y-2">
        <Segmented value={filter} onChange={setFilter} options={outcomeOptions(c)} />
        {tracking.since && (
          <p className="text-[11px] text-muted-foreground">
            Views are logged from {fmtDate(tracking.since)}, and only while analytics consent is on.
          </p>
        )}
      </div>
      <GestureTimeline key={filter} gestures={data.gestures} filter={filter} tracking={tracking} />
      {!data.gestures.some((g) => matchesOutcome(g, filter)) && (
        <EmptyState title="Nothing here" description="No gesture has this result." />
      )}
    </div>
  );
}

function BillingTab({ user: u, onSavePlan, saving }) {
  const [status, setStatus] = useState(planOf(u));
  const [trialEnds, setTrialEnds] = useState(u.trial_ends_at ? u.trial_ends_at.slice(0, 10) : '');
  useEffect(() => {
    setStatus(planOf(u));
    setTrialEnds(u.trial_ends_at ? u.trial_ends_at.slice(0, 10) : '');
  }, [u.subscription_status, u.trial_ends_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = status !== planOf(u) || (trialEnds || '') !== (u.trial_ends_at ? u.trial_ends_at.slice(0, 10) : '');

  return (
    <div className="space-y-6">
      <Block title="Purchase">
        <KeyValue items={[
          { label: 'Plan', value: PLAN_LABEL[planOf(u)] || humanize(planOf(u)) },
          { label: 'Product', value: humanize(u.plan_type) },
          { label: 'First payment', value: u.subscription_started_at ? fmtDate(u.subscription_started_at) : null },
          { label: 'Became premium', value: u.premium_upgraded_at ? fmtDate(u.premium_upgraded_at) : null },
          { label: 'Next renewal', value: u.current_period_end ? fmtDate(u.current_period_end) : null },
          { label: 'Trial ends', value: u.trial_ends_at ? fmtDate(u.trial_ends_at) : null },
          { label: 'Stripe customer', value: u.stripe_customer_id, mono: true },
        ]} />
      </Block>
      <Block title="Change plan">
        <p className="text-xs leading-relaxed text-muted-foreground">
          This changes access in the app only. It does not charge, refund or cancel anything in Stripe or the stores.
        </p>
        <Segmented
          value={status}
          onChange={setStatus}
          size="md"
          options={['free', 'premium', 'trial', 'expired'].map((p) => ({ value: p, label: PLAN_LABEL[p] }))}
        />
        {status === 'trial' && (
          <label className="block max-w-[220px]">
            <span className="mb-1 block text-xs text-muted-foreground">Trial ends on</span>
            <TextInput type="date" value={trialEnds} onChange={(e) => setTrialEnds(e.target.value)} />
          </label>
        )}
        <div className="flex gap-2">
          <Btn size="sm" disabled={!dirty || saving} onClick={() => onSavePlan(status, status === 'trial' ? trialEnds || null : null)}>
            {saving ? 'Saving…' : 'Save plan'}
          </Btn>
          {dirty && (
            <Btn size="sm" variant="ghost" onClick={() => { setStatus(planOf(u)); setTrialEnds(u.trial_ends_at ? u.trial_ends_at.slice(0, 10) : ''); }}>
              Cancel
            </Btn>
          )}
        </div>
      </Block>
    </div>
  );
}

function SystemTab({ user: u }) {
  const json = (label, value) => value && (
    <div key={label}>
      <p className="mb-1.5 text-xs text-muted-foreground">{label}</p>
      <pre className="max-h-64 overflow-auto rounded-lg border border-border bg-secondary/50 p-3 font-mono text-[11px] leading-relaxed">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
  return (
    <div className="space-y-6">
      <KeyValue items={[
        { label: 'User ID', value: u.user_id, mono: true },
        { label: 'Created', value: fmtDateTime(u.created_at) },
        { label: 'Updated', value: fmtDateTime(u.updated_at) },
        { label: 'Onboarding finished', value: u.onboarding_completed_at ? fmtDateTime(u.onboarding_completed_at) : yesNo(u.onboarding_completed) },
        { label: 'Push subscription', value: !u.push_subscription ? 'None'
          : u.push_subscription.active === false ? `Inactive since ${fmtDate(u.push_subscription.inactive_since)} (token no longer valid)`
          : 'Active' },
        { label: 'Analytics consent', value: u.analytics_opt_out ? `Withdrawn ${u.analytics_opt_out_at ? fmtDate(u.analytics_opt_out_at) : ''}` : 'Not withdrawn' },
      ]} />
      {json('Progressive profiling state', u.progressive_state)}
      {json('Special events', u.special_events)}
      {json('Push subscription', u.push_subscription)}
    </div>
  );
}

export default function CustomerSheet({ userId, initialTab, onClose }) {
  const { data: users, loading, setData, refetch } = useUsers();
  const { user: me } = useUser();
  const [confirm, confirmDialog] = useConfirm();
  const [tab, setTab] = useState('profile');
  const [busy, setBusy] = useState(false);

  const user = (users || []).find((u) => u.user_id === userId);
  const isSelf = !!me?.user_id && me.user_id === userId;

  useEffect(() => { setTab(TABS.includes(initialTab) ? initialTab : 'profile'); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchLocal = (changes) => setData((list) => (list || []).map((u) => (u.user_id === userId ? { ...u, ...changes } : u)));

  const run = async (action, { success, changes }) => {
    setBusy(true);
    try {
      await action();
      if (changes) patchLocal(changes);
      toast.success(success);
      refetch();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const savePlan = (plan, trialEndsAt) => run(
    () => api.patch(`/admin/users/${userId}/plan`, trialEndsAt ? { plan, trial_ends_at: trialEndsAt } : { plan }),
    { success: `Plan changed to ${PLAN_LABEL[plan]}`, changes: { subscription_status: plan, trial_ends_at: trialEndsAt || null } },
  );

  const setRole = async (role) => {
    const labels = {
      admin: { title: 'Make this person an admin?', description: 'Admins can see every customer, change plans and delete accounts.' },
      betatester: { title: 'Make this person a beta tester?', description: 'Beta testers get Premium for free and can use the app while beta mode is on.' },
      user: user?.role === 'betatester'
        ? { title: 'Remove beta tester access?', description: 'Their plan goes back to Free.' }
        : { title: 'Remove admin access?', description: 'They will no longer be able to open the admin console.' },
    };
    if (!(await confirm({ ...labels[role], confirmLabel: 'Confirm' }))) return;
    const changes = { role };
    if (role === 'betatester') changes.subscription_status = 'premium';
    if (role === 'user' && user?.role === 'betatester') changes.subscription_status = 'free';
    run(() => api.patch(`/admin/users/${userId}/role`, { role }), { success: `Role changed to ${ROLE_LABEL[role]}`, changes });
  };

  const toggleConsent = async () => {
    const next = !user.analytics_opt_out;
    const ok = await confirm(next
      ? {
        title: 'Withdraw analytics consent?',
        description: 'Use this when the person asked for it (GDPR). Their device resets its Firebase Analytics ID the next time they open the app.',
        confirmLabel: 'Withdraw consent',
      }
      : { title: 'Restore analytics consent?', description: 'Analytics can resume for this person.', confirmLabel: 'Restore' });
    if (!ok) return;
    run(
      () => api.patch(`/admin/users/${userId}/analytics-opt-out`, { opt_out: next }),
      { success: next ? 'Analytics consent withdrawn' : 'Analytics consent restored', changes: { analytics_opt_out: next } },
    );
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Delete ${displayName(user)}?`,
      description: 'This permanently deletes the account, sign-in, gestures, check-ins, notes, tickets and payment records. It cannot be undone, and it does not refund anything.',
      confirmLabel: 'Delete permanently',
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.del(`/admin/users/${userId}`);
      setData((list) => (list || []).filter((u) => u.user_id !== userId));
      if (res?.warnings?.length) toast.warning(`Deleted, but some data could not be removed: ${res.warnings.join('; ')}`);
      else toast.success('Customer deleted');
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const plan = planOf(user);
  const location = user ? [user.city, user.country].filter(Boolean).join(', ') : '';

  return (
    <>
      <Sheet open={!!userId} onOpenChange={(open) => { if (!open) onClose(); }}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
          {!user ? (
            <div className="p-6">
              <SheetTitle className="sr-only">Customer</SheetTitle>
              <SheetDescription className="sr-only">Customer details</SheetDescription>
              {loading || !users ? (
                <div className="space-y-3"><Skeleton className="h-12 w-64" /><Skeleton className="h-40 w-full" /></div>
              ) : (
                <EmptyState title="Customer not found" description="This account may have been deleted." />
              )}
            </div>
          ) : (
            <>
              <div className="border-b border-border px-5 pb-4 pt-5 sm:px-6">
                <div className="flex items-start gap-3.5 pr-8">
                  <Avatar user={user} size={44} />
                  <div className="min-w-0 flex-1">
                    <SheetTitle className="truncate text-base font-semibold">{displayName(user)}</SheetTitle>
                    <SheetDescription className="truncate text-sm">{user.email || 'No email'}</SheetDescription>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <Pill tone={PLAN_TONE[plan]}>{PLAN_LABEL[plan] || humanize(plan)}</Pill>
                      {user.role && user.role !== 'user' && <Pill tone="accent">{ROLE_LABEL[user.role] || user.role}</Pill>}
                      <Pill tone={user.onboarding_completed ? 'neutral' : 'warning'}>
                        {user.onboarding_completed ? 'Onboarded' : 'Not onboarded'}
                      </Pill>
                      {user.analytics_opt_out && <Pill tone="warning">Analytics withdrawn</Pill>}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />Joined {fmtDate(user.created_at)}</span>
                  {user.last_action_date && <span>Last gesture {timeAgo(user.last_action_date)}</span>}
                  {(location || user.country_code) && (
                    <span className="inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" />{countryFlag(user.country_code)} {location || user.country_code}</span>
                  )}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {user.email && (
                    <Btn variant="outline" size="sm" asChild>
                      <a href={`mailto:${user.email}`}><Mail className="h-3.5 w-3.5" />Email</a>
                    </Btn>
                  )}
                  <CopyButton text={user.user_id} label="Copy ID" className="h-8 border border-border px-3" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Btn variant="outline" size="sm" disabled={busy}><MoreHorizontal className="h-3.5 w-3.5" />More</Btn>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-60">
                      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                        {isSelf ? 'You can’t change your own role' : 'Access'}
                      </DropdownMenuLabel>
                      {user.role !== 'admin' && (
                        <DropdownMenuItem disabled={isSelf} onSelect={() => setRole('admin')}><ShieldCheck className="h-4 w-4" />Make admin</DropdownMenuItem>
                      )}
                      {user.role === 'admin' && (
                        <DropdownMenuItem disabled={isSelf} onSelect={() => setRole('user')}><ShieldOff className="h-4 w-4" />Remove admin</DropdownMenuItem>
                      )}
                      {user.role !== 'betatester' && (
                        <DropdownMenuItem disabled={isSelf} onSelect={() => setRole('betatester')}><UserCog className="h-4 w-4" />Make beta tester</DropdownMenuItem>
                      )}
                      {user.role === 'betatester' && (
                        <DropdownMenuItem disabled={isSelf} onSelect={() => setRole('user')}><UserMinus className="h-4 w-4" />Remove beta tester</DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={toggleConsent}>
                        <AlertTriangle className="h-4 w-4" />
                        {user.analytics_opt_out ? 'Restore analytics consent' : 'Withdraw analytics consent'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem disabled={isSelf} onSelect={remove} className="text-red-600 focus:text-red-600">
                        <Trash2 className="h-4 w-4" />Delete customer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
                <div className="border-b border-border px-5 py-2 sm:px-6">
                  <TabsList className="h-8 rounded-md">
                    {TABS.map((t) => (
                      <TabsTrigger key={t} value={t} className="rounded px-3 text-xs capitalize shadow-none data-[state=active]:shadow-none">
                        {t}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
                  <TabsContent value="profile" className="mt-0"><ProfileTab user={user} /></TabsContent>
                  <TabsContent value="activity" className="mt-0">{tab === 'activity' && <ActivityTab user={user} />}</TabsContent>
                  <TabsContent value="gestures" className="mt-0">{tab === 'gestures' && <GesturesTab user={user} />}</TabsContent>
                  <TabsContent value="billing" className="mt-0"><BillingTab user={user} onSavePlan={savePlan} saving={busy} /></TabsContent>
                  <TabsContent value="system" className="mt-0"><SystemTab user={user} /></TabsContent>
                </div>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>
      {confirmDialog}
    </>
  );
}
